/**
 * useAuth() — drop-in replacement for @sidebase/nuxt-auth.
 *
 * Reads the session from the @auth/core endpoint at /api/auth/session.
 * State is shared across all components via useState('auth-session') so
 * there is only one fetch per page load regardless of how many components call useAuth().
 *
 * API surface:
 *   status  — Ref<'loading' | 'authenticated' | 'unauthenticated'>
 *   data    — Ref<{ user: { id?, email?, name?, image? } } | null>
 *   signIn(provider, opts?) — redirects to /api/auth/signin/<provider>
 *   signOut(opts?)          — delegates to useZitadelSignOut()
 */

export interface AuthUser {
  id?: string;
  email?: string | null;
  name?: string | null;
  image?: string | null;
}

export interface AuthSession {
  user: AuthUser;
  expires: string;
}

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

// Module-level promise so the client fetch is initiated at most once per navigation.
let _clientFetch: Promise<void> | null = null;

function _resolveSession(
  s: AuthSession | null,
  data: Ref<AuthSession | null>,
  status: Ref<AuthStatus>
) {
  data.value   = s?.user ? s : null;
  status.value = s?.user ? 'authenticated' : 'unauthenticated';
}

export function useAuth() {
  const data   = useState<AuthSession | null>('auth-session', () => null);
  const status = useState<AuthStatus>('auth-status', () => 'loading');

  if (import.meta.server) {
    // SSR: fetch directly within the server context.
    // useAsyncData deduplicates within the same request.
    // We use server:true but getCachedData:false so the client always re-fetches
    // with its own cookies (cookie-based auth state is never correct server→client).
    const { data: result } = useAsyncData<AuthSession | null>(
      'auth:session',
      () => $fetch<AuthSession>('/api/auth/session').catch(() => null),
      {
        server: true,
        lazy: false,
        // Do NOT re-use the SSR payload on the client — auth depends on cookies
        // that differ between the server render and the client's actual session.
        getCachedData: () => null,
      }
    );
    watchEffect(() => _resolveSession(result.value ?? null, data, status));

  } else {
    // Client: initiate one fetch for the lifetime of this page.
    // _clientFetch is reset to null whenever navigateTo() is called (new page).
    if (!_clientFetch) {
      _clientFetch = $fetch<AuthSession>('/api/auth/session')
        .then((s)  => _resolveSession(s,    data, status))
        .catch(()  => _resolveSession(null, data, status));
    }
  }

  /**
   * Redirect to the @auth/core provider sign-in page.
   * Auth.js requires a CSRF-protected POST — a bare GET returns UnknownAction.
   * We fetch the CSRF token then submit a hidden form (same pattern as signin.vue)
   * so the browser follows all OAuth redirects as page navigations.
   */
  async function signIn(
    provider: string,
    opts: { callbackUrl?: string } = {}
  ) {
    const callbackUrl = opts.callbackUrl ?? (import.meta.client ? window.location.href : '/');

    if (import.meta.client) {
      // 1. Fetch CSRF token
      const { csrfToken } = await $fetch<{ csrfToken: string }>('/api/auth/csrf');

      // 2. Build and submit a form POST — browser follows OAuth redirects natively
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = `/api/auth/signin/${provider}`;

      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'csrfToken';
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);

      const callbackInput = document.createElement('input');
      callbackInput.type = 'hidden';
      callbackInput.name = 'callbackUrl';
      callbackInput.value = callbackUrl;
      form.appendChild(callbackInput);

      document.body.appendChild(form);
      form.submit();
    } else {
      // SSR: just navigate — the form submit will happen on the client after hydration
      return navigateTo(`/api/auth/signin/${provider}?callbackUrl=${encodeURIComponent(callbackUrl)}`, { external: true });
    }
  }

  /**
   * Sign out — clears local session state then delegates to Zitadel end_session.
   */
  async function signOut(opts: { callbackUrl?: string } = {}) {
    _clientFetch = null; // reset so next page re-fetches
    const { signOut: zitadelSignOut } = useZitadelSignOut();
    data.value   = null;
    status.value = 'unauthenticated';
    await zitadelSignOut(opts.callbackUrl ?? '/auth/signin');
  }

  return { data, status, signIn, signOut };
}
